/**
 * Workflow Step Block Schema
 *
 * A workflow is an edgeless document: each step is one block on the surface,
 * each edge one connector between two step blocks. The block ID is the step
 * ID, so the definition the runner gets is read straight from the document
 * (see the projection in `@streamlain/workflow-field-schemas`).
 *
 * `config` is a map with one entry per configuration key: two people who
 * change different keys of one step both keep their change.
 */

import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

export const WORKFLOW_STEP_FLAVOUR = 'affine:workflow-step';

type JsonRecord = Record<string, unknown>;

export type WorkflowStepBlockProps = {
  /** Step type of the runner, e.g. `code`, `loop`, `branch`. */
  stepType: string;
  /** Name shown in the editor and in run logs. */
  name: string;
  /** Configuration of the step, one entry per key. */
  config: JsonRecord;
  /** Bindings of upstream outputs to the inputs of this step; null when the step has none. */
  inputMappings: JsonRecord[] | null;
  /** Self-descriptive slots of the step; null when the step has none. */
  inputSlots: JsonRecord[] | null;
  configSlots: JsonRecord[] | null;
  outputSlots: JsonRecord[] | null;
  /** Workflow input schema on the start block; null on other steps. */
  inputSchema: JsonRecord | null;
  /** Workflow output schema on the end block; null on other steps. */
  outputSchema: JsonRecord | null;
} & GfxCompatibleProps;

export class WorkflowStepBlockModel extends GfxCompatible<WorkflowStepBlockProps>(
  BlockModel
) {}

export const WorkflowStepBlockSchema = defineBlockSchema({
  flavour: WORKFLOW_STEP_FLAVOUR,
  props: (): WorkflowStepBlockProps => ({
    stepType: '',
    name: '',
    config: {},
    inputMappings: null,
    inputSlots: null,
    configSlots: null,
    outputSlots: null,
    inputSchema: null,
    outputSchema: null,
    // As wide as the step card (WORKFLOW_STEP_WIDTH of the workflow schemas);
    // the canvas view sets the height to the card's.
    xywh: '[0,0,312,96]',
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'content',
    version: 1,
    parent: ['affine:surface'],
    children: [],
  },
  toModel: () => new WorkflowStepBlockModel(),
});

export const WorkflowStepBlockSchemaExtension = BlockSchemaExtension(
  WorkflowStepBlockSchema
);
